import React, { useState, useRef } from 'react';
import { portfolioLabAPI } from '../../../services/api';

const BulkUpload = ({ onImported }) => {
  const [show, setShow] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { committed, summary, portfolios }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const fileInputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setError('');
    setBusy(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const open = () => { reset(); setShow(true); };
  const close = () => { setShow(false); reset(); };

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    setError('');
    try {
      const response = await portfolioLabAPI.downloadBulkTemplate();
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'portfolio_lab_bulk_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to download template');
    } finally {
      setDownloading(false);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files?.[0] || null);
    setPreview(null);
    setError('');
  };

  const runUpload = async (commit) => {
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const result = await portfolioLabAPI.bulkUploadPortfolios(file, commit);
      setPreview(result);
      if (commit && result.summary.created > 0 && onImported) {
        onImported(result.summary.created);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const validCount = preview?.summary?.valid ?? 0;
  const committed = preview?.committed;

  return (
    <>
      <button
        className="btn btn-sm btn-outline-secondary"
        style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}
        onClick={open}
        title="Create multiple portfolios from an Excel file"
      >
        ⬆ Bulk Upload
      </button>

      {show && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={close}>
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header" style={{ background: 'var(--primary-color)', color: 'var(--white-color)' }}>
                <h5 className="modal-title" style={{ fontSize: '1rem' }}>Bulk Upload Portfolios</h5>
                <button type="button" className="btn-close btn-close-white" onClick={close} />
              </div>
              <div className="modal-body">
                <ol style={{ fontSize: '0.85rem', color: 'var(--gray-color)', paddingLeft: '1.1rem' }}>
                  <li className="mb-1">
                    Download the template and fill in the <strong>Portfolios</strong> and <strong>Holdings</strong> sheets.
                    Fund codes accept APIR or TR codes (see the <em>Funds (reference)</em> sheet).
                  </li>
                  <li className="mb-1">Upload it to preview. Fix any errors, then import.</li>
                </ol>

                <div className="d-flex gap-2 align-items-center mb-3 flex-wrap">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    style={{ fontSize: '0.8rem' }}
                    onClick={handleDownloadTemplate}
                    disabled={downloading}
                  >
                    {downloading ? 'Preparing…' : '⬇ Download Template'}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xlsm"
                    className="form-control form-control-sm"
                    style={{ maxWidth: '320px', fontSize: '0.8rem' }}
                    onChange={handleFileChange}
                  />
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    style={{ fontSize: '0.8rem' }}
                    onClick={() => runUpload(false)}
                    disabled={!file || busy}
                  >
                    {busy && !committed ? 'Checking…' : 'Preview'}
                  </button>
                </div>

                {error && (
                  <div className="alert alert-danger py-2" style={{ fontSize: '0.85rem' }}>
                    {error}
                  </div>
                )}

                {preview && (
                  <div>
                    <div
                      className={`alert py-2 ${committed ? 'alert-success' : validCount > 0 ? 'alert-info' : 'alert-warning'}`}
                      style={{ fontSize: '0.85rem' }}
                    >
                      {committed ? (
                        <>Imported <strong>{preview.summary.created}</strong> of {preview.summary.total} portfolio(s).
                          {preview.summary.invalid > 0 && <> {preview.summary.invalid} skipped due to errors.</>}</>
                      ) : (
                        <><strong>{validCount}</strong> of {preview.summary.total} portfolio(s) ready to import.
                          {preview.summary.invalid > 0 && <> {preview.summary.invalid} have errors and will be skipped.</>}</>
                      )}
                    </div>

                    <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                      <table className="table table-sm" style={{ fontSize: '0.8rem' }}>
                        <thead>
                          <tr>
                            <th style={{ width: '1.5rem' }} />
                            <th>Portfolio</th>
                            <th>Client</th>
                            <th className="text-end">Funds</th>
                            <th className="text-end">Weight</th>
                            <th>Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.portfolios.map((p, i) => {
                            const ok = p.valid;
                            return (
                              <tr key={i} style={{ background: ok ? undefined : 'rgba(255,193,7,0.08)' }}>
                                <td>{committed ? (p.created_id ? '✅' : (ok ? '' : '⏭')) : (ok ? '✅' : '⚠️')}</td>
                                <td>{p.name}</td>
                                <td>{p.client || '—'}</td>
                                <td className="text-end">{p.fund_count}</td>
                                <td className="text-end">{p.total_weight != null ? `${p.total_weight.toFixed(1)}%` : '—'}</td>
                                <td>
                                  {p.errors?.length > 0 && (
                                    <div className="text-danger">{p.errors.join(' ')}</div>
                                  )}
                                  {p.warnings?.length > 0 && (
                                    <div className="text-muted">{p.warnings.join(' ')}</div>
                                  )}
                                  {p.errors?.length === 0 && p.warnings?.length === 0 && (
                                    <span className="text-muted">
                                      {p.objective_type && p.return_objective != null
                                        ? `Objective: ${p.objective_type === 'absolute' ? '' : p.objective_type === 'cash' ? 'Cash+' : 'CPI+'}${(p.return_objective * 100).toFixed(2)}%`
                                        : ''}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={close}>
                  {committed ? 'Close' : 'Cancel'}
                </button>
                {preview && !committed && validCount > 0 && (
                  <button
                    className="btn btn-sm"
                    style={{ background: 'var(--primary-color)', color: 'var(--white-color)' }}
                    onClick={() => runUpload(true)}
                    disabled={busy}
                  >
                    {busy ? 'Importing…' : `Import ${validCount} Portfolio${validCount !== 1 ? 's' : ''}`}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BulkUpload;
